import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ModalToast from './ModalToast';

const SignInModal = ({ isOpen, onClose }) => {
  const { signIn, sendOtp } = useAuth();
  const [step, setStep] = useState('mobile'); // 'mobile' or 'otp'
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [otpValidated, setOtpValidated] = useState(false);
  const [modalToast, setModalToast] = useState({ message: '', type: '', isVisible: false });

  // Modal toast helpers
  const hideModalToast = () => {
    setModalToast(prev => ({ ...prev, isVisible: false }));
  };

  const resetForm = useCallback(() => {
    setStep('mobile');
    setMobileNumber('');
    setOtp('');
    setError('');
    setLoading(false);
    setOtpTimer(0);
    setCanResend(true);
    setOtpValidated(false);
    hideModalToast();
    setCanResend(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const handleClose = useCallback(() => {
    if (!loading) {
      resetForm();
      onClose();
    }
  }, [loading, onClose, resetForm]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      resetForm();
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, resetForm]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    
    // Field validation with specific messages
    if (!mobileNumber.trim()) {
      const errorMsg = 'Please enter mobile number';
      setError(errorMsg);
      return;
    }
    
    if (!/^\d{10,15}$/.test(mobileNumber)) {
      const errorMsg = 'Please enter a valid mobile number (10-15 digits)';
      setError(errorMsg);
      return;
    }

    setLoading(true);
    setError('');
    hideModalToast();

    try {
      const result = await sendOtp(mobileNumber);
      
      if (result.success) {
        setStep('otp');
        setOtpTimer(10);
        setCanResend(false);
      } else {
        const errorMsg = result.error || 'Failed to send OTP';
        setError(errorMsg);
      }
    } catch (error) {
      const errorMsg = 'Failed to send OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    
    // Field validation with specific messages
    if (!otp.trim()) {
      const errorMsg = 'Please enter OTP';
      setError(errorMsg);
      return;
    }
    
    if (!/^\d{4}$/.test(otp)) {
      const errorMsg = 'Please enter a valid 4-digit OTP';
      setError(errorMsg);
      return;
    }

    setLoading(true);
    setError('');
    hideModalToast();

    try {
      const result = await signIn(mobileNumber, otp);
      
      if (result.success) {
        setOtpValidated(true);
        // Small delay to show success state before closing
        setTimeout(() => {
          onClose();
          resetForm();
        }, 2000);
      } else {
        // Show a single, consistent error message at the top only
        setError('OTP is incorrect. Please enter the correct OTP');
      }
    } catch (error) {
      // Also normalize unexpected errors to the same OTP incorrect message per requirement
      setError('OTP is incorrect. Please enter the correct OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    setCanResend(false);
    setError('');
    hideModalToast();
    
    try {
      const result = await sendOtp(mobileNumber);
      
      if (result.success) {
        setOtpTimer(10);
      } else {
        const errorMsg = result.error || 'Failed to resend OTP';
        setError(errorMsg);
        setCanResend(true);
      }
    } catch (error) {
      const errorMsg = 'Failed to resend OTP. Please try again.';
      setError(errorMsg);
      setCanResend(true);
    }
  };


  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10001,
      padding: '1rem'
    }} onClick={handleClose}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '420px',
        padding: '2.5rem',
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(10px)'
      }} onClick={e => e.stopPropagation()}>
        {/* Modal Toast */}
        <ModalToast
          message={modalToast.message}
          type={modalToast.type}
          isVisible={modalToast.isVisible}
          onClose={hideModalToast}
          duration={3000}
        />
        
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '1.5rem',
            right: '1.5rem',
            background: '#f8fafc',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            cursor: 'pointer',
            color: '#64748b',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#e2e8f0';
            e.target.style.color = '#374151';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#f8fafc';
            e.target.style.color = '#64748b';
          }}
        >
          <i className="fas fa-times"></i>
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)'
          }}>
            <i className="fas fa-user" style={{
              fontSize: '2rem',
              color: 'white'
            }}></i>
          </div>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '0.75rem',
            letterSpacing: '-0.025em'
          }}>
            Sign In to Your Account
          </h2>
          <p style={{ 
            color: '#64748b', 
            fontSize: '0.95rem',
            lineHeight: '1.5',
            margin: '0'
          }}>
            {step === 'mobile' 
              ? 'Enter your mobile number to get started'
              : `We've sent a 4-digit code to your mobile number ${mobileNumber}`
            }
          </p>
        </div>
        
        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}
        
        {/* Mobile input */}
        {step === 'mobile' && (
          <form onSubmit={handleSendOtp}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Mobile Number
              </label>
              <input
                type="tel"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter mobile number"
                maxLength="15"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: '#f9fafb',
                  outline: 'none',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? '#94a3b8' : '#2563eb',
                color: 'white',
                padding: '0.75rem',
                borderRadius: '8px',
                border: 'none',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Sending OTP...
                </>
              ) : (
                <>
                  Send OTP
                  <i className="fas fa-arrow-right"></i>
                </>
              )}
            </button>
          </form>
        )}

        {/* OTP input */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            {/* OTP Status Card */}
            <div style={{
              background: otpValidated ? 
                'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)' :
                loading && !error ? 
                  'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)' :
                  'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
              border: otpValidated ?
                '1px solid #bbf7d0' :
                loading && !error ? 
                  '1px solid #bfdbfe' : 
                  '1px solid #bbf7d0',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: otpValidated ?
                  'linear-gradient(135deg, #10b981 0%, #059669 100%)' :
                  loading && !error ?
                    'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)' :
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '1.1rem'
              }}>
                {loading && !error && !otpValidated ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className="fas fa-check"></i>
                )}
              </div>
              <div>
                <p style={{
                  margin: '0',
                  color: otpValidated ? '#065f46' : loading && !error ? '#1e40af' : '#065f46',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}>
                  {otpValidated ? 'OTP Validated Successfully!' : 
                   loading && !error ? 'Validating OTP...' : 'OTP Sent Successfully!'}
                </p>
                <p style={{
                  margin: '0',
                  color: otpValidated ? '#047857' : loading && !error ? '#1d4ed8' : '#047857',
                  fontSize: '0.8rem',
                  marginTop: '0.25rem'
                }}>
                  {otpValidated ? 'You will be signed in shortly...' :
                   loading && !error ? 
                     'Please wait while we verify your code' :
                     ''
                  }
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                color: '#374151',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                Enter OTP
              </label>
              
              {/* Single OTP Input Field */}
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="Enter 4-digit OTP"
                maxLength="4"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  textAlign: 'center',
                  letterSpacing: '0.2rem',
                  background: '#f9fafb',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                required
              />
            </div>

            {/* Resend OTP */}
            <div style={{
              textAlign: 'center',
              marginBottom: '1.5rem'
            }}>
              {otpTimer > 0 ? (
                <span style={{
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>Resend OTP in {otpTimer}s</span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={!canResend}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: canResend ? '#2563eb' : '#9ca3af',
                    cursor: canResend ? 'pointer' : 'not-allowed',
                    fontSize: '0.875rem',
                    textDecoration: canResend ? 'underline' : 'none'
                  }}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: loading ? '#94a3b8' : '#2563eb',
                color: 'white',
                padding: '0.75rem',
                borderRadius: '8px',
                border: 'none',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Verifying...
                </>
              ) : (
                <>
                  Verify & Sign In
                  <i className="fas fa-check"></i>
                </>
              )}
            </button>

            {/* Back button */}
            <button
              type="button"
              onClick={() => {
                setStep('mobile');
                setOtp('');
                setError('');
                setOtpTimer(0);
                setCanResend(false);
              }}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#6b7280',
                padding: '0.75rem',
                border: 'none',
                fontSize: '0.875rem',
                cursor: 'pointer',
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <i className="fas fa-arrow-left"></i>
              Change Mobile Number
            </button>
          </form>
        )}

        {/* Terms */}
        <p style={{
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: '0.75rem',
          marginTop: '1.5rem'
        }}>
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default SignInModal;
